import axios from 'axios';
import { AddressSuggestionItem, getAddressSuggestions } from '@/shared/api/suggestions';
import { AddressSuggestionsFieldProps } from './types';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';

const AddressSuggestionsField = (props: AddressSuggestionsFieldProps) => {
  const {
    className,
    disabled,
    error,
    helperText,
    id,
    isDisabled,
    label,
    onBlur,
    onClearSelection,
    onSelectSuggestion,
    onTextChange,
    placeholder,
    required,
    scope = 'warehouse',
    value
  } = props;

  const isFieldDisabled = isDisabled ?? disabled;
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestionItem[]>([]);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasError = Boolean(error);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const nextValue = event.target.value;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const currentRequestId = ++requestIdRef.current;
    setSuggestions([]);
    setSuggestionsError(null);
    setIsOpen(false);
    onTextChange(nextValue);
    onClearSelection?.();

    if (nextValue.trim().length < 3) {
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const data = await getAddressSuggestions(
          {
            count: 10,
            query: nextValue.trim(),
            scope
          },
          controller.signal
        );

        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setSuggestions(data);
        setSuggestionsError(null);
        setIsOpen(true);
      } catch (requestError) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        if (axios.isCancel(requestError) || (requestError instanceof DOMException && requestError.name === 'AbortError')) {
          return;
        }

        if (axios.isAxiosError(requestError) && requestError.response?.status === 503) {
          setSuggestionsError('Подсказки временно недоступны, попробуйте позже');
          setSuggestions([]);
          setIsOpen(true);
          return;
        }

        setSuggestions([]);
        setIsOpen(false);
      }
    }, 300);
  };

  const onInputFocus = (): void => {
    if (suggestions.length > 0 || suggestionsError) {
      setIsOpen(true);
    }
  };

  const onSelectItem = (suggestion: AddressSuggestionItem): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    requestIdRef.current += 1;
    onSelectSuggestion(suggestion);
    setIsOpen(false);
    setSuggestions([]);
    setSuggestionsError(null);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      requestIdRef.current += 1;
    };
  }, []);

  return (
    <div
      className={cn('relative flex w-full flex-col gap-2', className)}
      ref={containerRef}
    >
      {label ? (
        <label
          className="truncate font-sans text-sm font-normal leading-5 text-muted-foreground"
          htmlFor={id}
        >
          {label}
        </label>
      ) : null}
      <div className="relative w-full">
        <Input
          className={cn(
            'font-sans text-base font-normal leading-6 md:text-base [&_input]:text-base [&_input]:font-normal [&_input]:leading-6 [&_input]:md:text-base',
            !isFieldDisabled && 'border-border-strong'
          )}
          autoComplete="off"
          disabled={isFieldDisabled}
          error={hasError}
          id={id}
          onBlur={onBlur}
          onChange={onInputChange}
          onFocus={onInputFocus}
          placeholder={placeholder}
          required={required}
          value={value}
        />
      </div>
      {error ? (
        <span className="px-3 font-sans text-xs font-normal leading-4 text-[hsl(var(--error-text))]">
          {error}
        </span>
      ) : helperText ? (
        <span className="px-3 font-sans text-xs font-normal leading-4 text-muted-foreground">
          {helperText}
        </span>
      ) : null}
      {isOpen && (suggestions.length > 0 || suggestionsError) ? (
        <ul
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-2xl border border-border-strong bg-surface p-1 shadow-lg"
          role="listbox"
        >
          {suggestionsError ? (
            <li className="px-4 py-3 text-xs leading-4 text-muted-foreground">
              {suggestionsError}
            </li>
          ) : (
            suggestions.map((suggestion, index) => (
              <li
                className="cursor-pointer rounded-xl px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-accent-soft/60"
                key={`${suggestion.fias_id ?? suggestion.fiasId ?? index}-${suggestion.value}`}
                onClick={() => onSelectItem(suggestion)}
                role="option"
              >
                {suggestion.value}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};

export default AddressSuggestionsField;